import React, { useContext, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Mail } from "lucide-react";
import { LanguageContext } from "../../constant/LanguageContext";
import { legalTranslations, PRIVACY_EMAIL } from "./legalTranslations";
import logoDark from "../../Images/English Nexploy V2.png";
import "./Legal.css";

/** Returns the language-matched content and the app-wide language state.
 *  A `?lang=en|ar` query param (used by store listings and the mobile app
 *  when they link here) switches the shared LanguageContext once on load. */
export function useLegalContent() {
  const { language, toggleLanguage } = useContext(LanguageContext);
  const [params] = useSearchParams();
  const requested = params.get("lang");

  useEffect(() => {
    if ((requested === "en" || requested === "ar") && requested !== language) {
      toggleLanguage({ target: { value: requested } });
    }
    // Only honour the param on arrival; afterwards the switch decides.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requested]);

  const lang = language === "ar" ? "ar" : "en";
  return { lang, t: legalTranslations[lang], toggleLanguage };
}

export function EmailLink({ subject }) {
  const href = subject
    ? `mailto:${PRIVACY_EMAIL}?subject=${encodeURIComponent(subject)}`
    : `mailto:${PRIVACY_EMAIL}`;
  return (
    <a className="nxlg-email" href={href}>
      <Mail size={16} aria-hidden="true" />
      <bdi>{PRIVACY_EMAIL}</bdi>
    </a>
  );
}

/** Shared chrome for the public legal pages: brand header with an EN/AR
 *  switch, a readable single-column body and a footer linking both pages. */
export default function LegalLayout({ docTitle, children }) {
  const { lang, t, toggleLanguage } = useLegalContent();
  const BackIcon = lang === "ar" ? ArrowRight : ArrowLeft;

  useEffect(() => {
    const previous = document.title;
    document.title = docTitle;
    return () => {
      document.title = previous;
    };
  }, [docTitle]);

  const setLang = (value) => {
    if (value !== lang) toggleLanguage({ target: { value } });
  };

  return (
    <div className="nxlg" dir={t.dir} lang={lang}>
      <header className="nxlg-header">
        <div className="nxlg-container nxlg-header-inner">
          <Link to="/" className="nxlg-brand" aria-label="Nexploy">
            <img src={logoDark} alt="Nexploy" width="150" height="37" />
          </Link>
          <div className="nxlg-lang" role="group" aria-label={t.common.langLabel}>
            <button
              type="button"
              className={lang === "en" ? "is-active" : ""}
              aria-pressed={lang === "en"}
              onClick={() => setLang("en")}
            >
              EN
            </button>
            <button
              type="button"
              className={lang === "ar" ? "is-active" : ""}
              aria-pressed={lang === "ar"}
              onClick={() => setLang("ar")}
            >
              عربي
            </button>
          </div>
        </div>
      </header>

      <main className="nxlg-main">
        <div className="nxlg-container">
          <Link to="/" className="nxlg-back">
            <BackIcon size={16} aria-hidden="true" />
            {t.common.backHome}
          </Link>
          {children({ lang, t })}
        </div>
      </main>

      <footer className="nxlg-footer">
        <div className="nxlg-container nxlg-footer-inner">
          <p>{t.common.rights(new Date().getFullYear())}</p>
          <nav aria-label="Legal">
            <Link to="/privacy">{t.common.privacyLink}</Link>
            <Link to="/delete-account">{t.common.deleteLink}</Link>
            <a href={`mailto:${PRIVACY_EMAIL}`}>
              <bdi>{PRIVACY_EMAIL}</bdi>
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
